import type { Coding } from '@medplum/fhirtypes'
import { LOINC_CODE_SYSTEM } from '#src/codeSystems.ts'
import type { CodingDetails, ObservationTemplate } from '#src/codings.ts'
import {
  BLOOD_PRESSURE_CODING,
  DIASTOLIC_BLOOD_PRESSURE_CODING,
  loincCodingOf,
  SYSTOLIC_BLOOD_PRESSURE_CODING,
} from '#src/models/loinc.ts'

const MM_HG = 'mm[Hg]'

// LOINC's panels carry no member list over `$lookup`, so the parts of a panel
// we record are written here.
export const BLOOD_PRESSURE_TEMPLATE: ObservationTemplate = {
  parent: loincCodingOf(BLOOD_PRESSURE_CODING),
  format: '{8480-6}/{8462-4}',
  components: [
    {
      coding: loincCodingOf(SYSTOLIC_BLOOD_PRESSURE_CODING),
      label: 'Systolic',
      short: 'Sys',
      unit: MM_HG,
      scaleType: 'Qn',
      required: true,
    },
    {
      coding: loincCodingOf(DIASTOLIC_BLOOD_PRESSURE_CODING),
      label: 'Diastolic',
      short: 'Dia',
      unit: MM_HG,
      scaleType: 'Qn',
      required: true,
    },
    {
      coding: {
        system: LOINC_CODE_SYSTEM,
        code: '8361-8',
        display: 'Body position with respect to gravity',
      },
      label: 'Position',
      scaleType: 'Nom',
      answerOptions: [
        { system: LOINC_CODE_SYSTEM, code: 'LA11868-9', display: 'Sitting' },
        { system: LOINC_CODE_SYSTEM, code: 'LA11870-5', display: 'Standing' },
        { system: LOINC_CODE_SYSTEM, code: 'LA11869-7', display: 'Lying' },
      ],
      required: false,
    },
    {
      coding: {
        system: LOINC_CODE_SYSTEM,
        code: '41904-4',
        display: 'Blood pressure measurement site',
      },
      label: 'Site',
      scaleType: 'Nom',
      // LOINC answer list LL718-8, as Medplum's $lookup returns it.
      answerOptions: [
        {
          system: LOINC_CODE_SYSTEM,
          code: 'LA11159-3',
          display: 'Biceps right',
        },
        {
          system: LOINC_CODE_SYSTEM,
          code: 'LA11158-5',
          display: 'Biceps left',
        },
        {
          system: LOINC_CODE_SYSTEM,
          code: 'LA11161-9',
          display: 'Wrist right',
        },
        { system: LOINC_CODE_SYSTEM, code: 'LA11160-1', display: 'Wrist left' },
        {
          system: LOINC_CODE_SYSTEM,
          code: 'LA21900-8',
          display: 'Thigh right',
        },
        { system: LOINC_CODE_SYSTEM, code: 'LA21901-6', display: 'Thigh left' },
        {
          system: LOINC_CODE_SYSTEM,
          code: 'LA21902-4',
          display: 'Ankle right',
        },
        { system: LOINC_CODE_SYSTEM, code: 'LA21903-2', display: 'Ankle left' },
        { system: LOINC_CODE_SYSTEM, code: 'LA11862-2', display: 'Finger' },
      ],
      required: false,
    },
    {
      coding: {
        system: LOINC_CODE_SYSTEM,
        code: '8478-0',
        display: 'Mean blood pressure',
      },
      label: 'Mean arterial pressure',
      short: 'MAP',
      unit: MM_HG,
      scaleType: 'Qn',
      required: false,
    },
  ],
}

export const OBSERVATION_TEMPLATES: readonly ObservationTemplate[] = [
  BLOOD_PRESSURE_TEMPLATE,
]

// The template a code is recorded with, from the registry given (the
// source registry by default). Undefined for a code recorded as one value.
export function observationTemplate(
  coding: Pick<Coding, 'system' | 'code'>,
  templates: readonly ObservationTemplate[] = OBSERVATION_TEMPLATES
): ObservationTemplate | undefined {
  if (!coding.system || !coding.code) {
    return undefined
  }
  return templates.find(
    (template) =>
      template.parent.system === coding.system &&
      template.parent.code === coding.code
  )
}

// A code recorded as components is a `Qn` with no unit of its own.
export function templateDetails(
  template: ObservationTemplate
): Omit<CodingDetails, 'display'> {
  const details: Omit<CodingDetails, 'display'> = {
    scaleType: 'Qn',
    unit: null,
    answerOptions: [],
    components: template.components,
  }
  if (template.format) {
    details.format = template.format
  }
  return details
}
